import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import "./post.scss";
import LatestData from "../../data/latest.json";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  runTransaction,
} from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import Sprite from "../../../public/SVG/sprite.svg";
import Logo from "../../../public/img/ale-baja-logo-three-colors.svg";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Helmet from "react-helmet";


async function ensureAnonUser() {
  if (!auth.currentUser) {
    await signInAnonymously(auth);
  }
  return auth.currentUser.uid;
}

async function addCommentForSlug(slug, authorId, authorName, text) {
  if (!slug || !text?.trim() || !authorId) return;
  await addDoc(collection(db, "comments"), {
    slug,
    authorId,
    authorName,
    text: text.trim(),
    createdAt: serverTimestamp(),
  });
}

function subscribeComments(slug, cb) {
  const q = query(
      collection(db, "comments"),
      where("slug", "==", slug),
      orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) =>
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
}

async function toggleLikeForSlug(slug, userId) {
  if (!slug || !userId) return null;
  const likeId = `${slug}_${userId}`;
  const likeRef = doc(db, "likes", likeId);
  const countRef = doc(db, "likes_count", slug);

  const likeSnap = await getDoc(likeRef);
  if (likeSnap.exists()) {
    await deleteDoc(likeRef);
    await runTransaction(db, async (t) => {
      const s = await t.get(countRef);
      if (!s.exists()) return;
      const current = s.data().count || 0;
      t.update(countRef, { count: Math.max(0, current - 1) });
    });
    return false;
  } else {
    await setDoc(likeRef, { slug, userId, createdAt: serverTimestamp() });
    await runTransaction(db, async (t) => {
      const s = await t.get(countRef);
      if (!s.exists()) t.set(countRef, { count: 1 });
      else t.update(countRef, { count: (s.data().count || 0) + 1 });
    });
    return true;
  }
}

function subscribeLikesCount(slug, cb) {
  const ref = doc(db, "likes_count", slug);
  return onSnapshot(ref, (snap) => cb(snap.exists() ? snap.data().count || 0 : 0));
}


export default function Post() {
  const params = useParams();
  const location = useLocation();
  const title = params.title;

  const [fontSize, setFontSize] = useState(24);
  const [visible, setVisible] = useState(false);
  const [printLogo, setPrintLogo] = useState(false);
  const [posts] = useState([]); // statyczne treści
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [likesCount, setLikesCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [authorName, setAuthorName] = useState("");

  const userIdRef = useRef(null); // <- będzie stringiem po ensureAnonUser
  const unsubCommentsRef = useRef(null);
  const unsubLikesRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setVisible(window.pageYOffset > 120);
    window.addEventListener("scroll", onScroll);

    let mounted = true;

    ensureAnonUser().then((uid) => {
      if (mounted) userIdRef.current = uid;
      setupForSlug();
    });

    return () => {
      mounted = false;
      window.removeEventListener("scroll", onScroll);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  function cleanup() {
    if (unsubCommentsRef.current) unsubCommentsRef.current();
    if (unsubLikesRef.current) unsubLikesRef.current();
  }

  function findPostData() {
    const all = [...posts, ...LatestData.content];

    for (const item of all) {
      if (item?.slug === title) return item;
      if (item && typeof item === "object" && item[title]) {
        return {
          slug: title,
          ...item[title],
        };
      }
    }
    return null;
  }

  async function setupForSlug() {
    cleanup();
    if (!userIdRef.current) return;

    const postData = findPostData();
    if (!postData) return;
    const slug = postData.slug;
    if (!slug) return;

    unsubCommentsRef.current = subscribeComments(slug, setComments);
    unsubLikesRef.current = subscribeLikesCount(slug, setLikesCount);

    const likeId = `${slug}_${userIdRef.current}`;
    const likeSnap = await getDoc(doc(db, "likes", likeId));
    setLiked(!!likeSnap.exists());
  }

  async function handleToggleLike() {
    if (!userIdRef.current) return;

    const postData = findPostData();
    if (!postData) return;
    const slug = postData.slug;
    if (!slug) return;

    const newState = await toggleLikeForSlug(slug, userIdRef.current);
    if (newState !== null) setLiked(newState);
  }

  async function handleAddComment(e) {
    e?.preventDefault();
    if (!userIdRef.current) return;

    const postData = findPostData();
    if (!postData) return;
    const slug = postData.slug;
    if (!slug) return;
    if (!commentText.trim()) return;

    await addCommentForSlug(slug, userIdRef.current, authorName || "Anonimowy użytkownik", commentText);
    setCommentText("");
    setAuthorName("");
  }

  const postData = findPostData();
  if (!postData) return <p>Post nie znaleziony...</p>;

  const contentData = postData[title] || postData;
  const image =
      postData.imageUrl || `/img/${postData.slug || title}.jpg` || `/img/jak-to-wszystko-sie-zaczelo.jpg`;
  const displayHeader = contentData.header || contentData.title;
  const displayContent = contentData.content || "";

  function print(element) {
    const printContents = document.getElementsByClassName(element)[0].innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    setPrintLogo(false);
    window.location.reload();
  }

  return (
      <div className="post">
        <Helmet>
          <meta charSet="utf-8"/>
          <title>Alebaja - {displayHeader}</title>
        </Helmet>

        <Breadcrumbs className="content__breadcrumb no-print" aria-label="breadcrumb">
          <Link color="inherit" href="/">
            Strona główna
          </Link>
          <Link color="inherit" href={location.pathname.includes("/bajki/") ? "/bajki" : "/artykuly"}>
            {location.pathname.includes("/bajki/") ? "Bajki" : "Artykuły"}
          </Link>
          <Typography color="textPrimary">{displayHeader}</Typography>
        </Breadcrumbs>

        {printLogo && <img className="navbar-logo" src={Logo} alt="logo"/>}

        <div className="content__button">
          <button
              className="content__button--print no-print"
              onClick={() => {
                setPrintLogo(true);
                setTimeout(() => print("post"), 20);
              }}
          >
            <svg className="content__icon">
              <use href={Sprite + "#icon-printer"}></use>
            </svg>
            Drukuj {location.pathname.includes("/bajki/") ? "bajkę" : "artykuł"}
          </button>
        </div>

        <div className={visible ? "visible no-print" : "hidden no-print"}>
          <a className="font-utility font-utility__increment" onClick={() => setFontSize((s) => s + 1)}>
            +
          </a>
          <a className="font-utility font-utility__decrement" onClick={() => setFontSize((s) => s - 1)}>
            -
          </a>
        </div>

        <h1 className="content__title">{displayHeader}</h1>
        <img className="content__image" src={image} alt="drobinek"/>
        <div
            className="content__text"
            dangerouslySetInnerHTML={{__html: displayContent}}
            style={{fontSize: fontSize + "px"}}
        ></div>

        <div className="no-print post-interactions">
          <button
              onClick={handleToggleLike}
              className={`like-btn ${liked ? "liked" : ""}`}
          >
            <span className="like-icon">👍</span>
            {liked ? "Lubię to" : "Polub"} ({likesCount})
          </button>

          <div className="comments">
            <form onSubmit={handleAddComment} className="comment-form">
              <input
                  className="comment-author"
                  value={authorName} // nowy state
                  onChange={(e) => setAuthorName(e.target.value)}
                  placeholder="Twoje imię"
              />
              <textarea
                  className="comment-text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Napisz komentarz..."
              />
              <button type="submit" className="comment-submit">Dodaj</button>
            </form>

            <ul className="comments-list">
              {comments.map((c) => (
                  <li key={c.id} className="comment-item">
                    <div className="comment-header">
                      <b>{c.authorName || "Anon"}</b>
                      <span className="comment-date">
              {c.createdAt?.toDate
                  ? c.createdAt.toDate().toLocaleString()
                  : ""}
            </span>
                    </div>
                    <div className="comment-body">{c.text}</div>
                  </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
  );
}
